import { CreateDoctorInfoMapper } from "@/data/mapper/admin/create-doctor-info-mapper";
import { DoctorDetailInfoMapper } from "@/data/mapper/admin/doctor-detail-info-mapper";
import { DoctorListMapper } from "@/data/mapper/admin/doctor-list-mapper";
import { DoctorCreateRequestPayload } from "@/data/payload/admin/doctor-create-request-payload";
import { DoctorUpdateRequestPayload } from "@/data/payload/admin/doctor-update-reqeust-payload";
import { GetDoctorRequestPayload } from "@/data/payload/admin/get-doctor-request-payload";
import { safeMapToEntity } from "@/data/repo-Implement/safe-map-to-entity";
import { AdminSource } from "@/data/source/admin-source";
import { AdminPatientTableInfoEntity } from "@/domain/entity/admin/admin-patient-table-info-entity";
import { CreateDoctorReponseInfoEntity } from "@/domain/entity/admin/create-doctor-reponse-info-entity";
import { DoctorDetailInfoEntity } from "@/domain/entity/admin/doctor-detail-info-entity";
import { DoctorInfoEntity } from "@/domain/entity/admin/doctor-info-entity";
import { AdminRepository } from "@/domain/repository/admin-repository";
import { SATError, SATResult } from "@sadtx/sat-error";
import { AdminPatientListResponseMapper } from "../mapper/patient/admin-patient-list-mapper";

/**
 * 관리자 관련 레포지토리 구현체
 */
export class AdminRepositoryImpl implements AdminRepository {
  constructor(private readonly adminSource: AdminSource) {}

  /**
   * 의사 목록을 조회합니다
   * @returns {Promise<SATResult<DoctorInfoEntity[], SATError>>} 의사 목록 정보
   */
  async fetchDoctorList(): Promise<SATResult<DoctorInfoEntity[], SATError>> {
    const result = await this.adminSource.getDoctorList();

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      DoctorListMapper.toEntityList,
      "AdminRepositoryImpl::fetchDoctorList"
    );
  }

  /**
   * 의사를 등록합니다
   * @param createData - 등록할 의사 정보
   * @returns {Promise<SATResult<DoctorInfoEntity, SATError>>} 등록된 의사 정보
   */
  async createDoctor(
    createData: DoctorCreateRequestPayload
  ): Promise<SATResult<CreateDoctorReponseInfoEntity, SATError>> {
    const result = await this.adminSource.createDoctor(createData);

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      CreateDoctorInfoMapper.toEntity,
      "AdminRepositoryImpl::createDoctor"
    );
  }

  /**
   * 환자 목록을 조회합니다
   * @returns {Promise<SATResult<AdminPatientTableInfoEntity[], SATError>>} 환자 목록 정보
   */
  async fetchPatientList(): Promise<SATResult<AdminPatientTableInfoEntity[], SATError>> {
    const result = await this.adminSource.getPatientList();

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      AdminPatientListResponseMapper.toEntityList,
      "AdminRepositoryImpl::fetchPatientList"
    );
  }

  /**
   * 의사 정보를 조회합니다
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<DoctorDetailInfoEntity, SATError>>} 의사 정보
   */
  async fetchDoctor(
    payload: GetDoctorRequestPayload
  ): Promise<SATResult<DoctorDetailInfoEntity, SATError>> {
    const result = await this.adminSource.getDoctor(payload);

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return safeMapToEntity(
      result.value,
      DoctorDetailInfoMapper.toEntity,
      "AdminRepositoryImpl::fetchDoctor"
    );
  }

  /**
   * 의사 정보를 업데이트합니다
   * @param doctorUuid - 의사 UUID
   * @param updateData - 업데이트할 의사 정보
   * @returns {Promise<SATResult<DoctorUpdateResponseDto, SATError>>} 업데이트된 의사 정보
   */
  async updateDoctor(
    doctorUuid: string,
    updateData: DoctorUpdateRequestPayload
  ): Promise<SATResult<void, SATError>> {
    const result = await this.adminSource.updateDoctor(doctorUuid, updateData);

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return SATResult.success(result.value);
  }

  /**
   * 의사 계정을 활성화합니다
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<void, SATError>>} 활성화 결과
   */
  async activateDoctor(doctorUuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.adminSource.activateDoctor(doctorUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return SATResult.success(result.value);
  }

  /**
   * 의사 계정을 비활성화합니다
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<void, SATError>>} 비활성화 결과
   */
  async deactivateDoctor(doctorUuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.adminSource.deactivateDoctor(doctorUuid);

    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return SATResult.success(result.value);
  }

  /**
   * 비밀번호 설정 메시지 재전송합니다
   */

  async sendPasswordChangeEmail(doctorUuid: string): Promise<SATResult<void, SATError>> {
    const result = await this.adminSource.sendPasswordChangeEmail(doctorUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }

    return SATResult.success(result.value);
  }
}
